import { hubPageBg } from './accountHubStyles';
import { useAsUserParam } from './useAsUserParam';
import { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setSessions,
  setSessionsLoading,
  removeSession,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import type { ActiveSession } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import useAuth from 'src/hooks/useAuth';
import axios from 'src/utils/axios';

function getDeviceIcon(label: string): typeof DevicesIcon {
  const lower = label.toLowerCase();
  if (
    lower.includes('mobile') ||
    lower.includes('iphone') ||
    lower.includes('android')
  ) {
    return PhoneAndroidIcon;
  }
  return LaptopMacIcon;
}

function formatRelative(iso: string | null): string {
  if (!iso) {
    return 'Just now';
  }
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return 'Just now';
  }
  if (mins < 60) {
    return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  }
  return new Date(iso).toLocaleDateString();
}

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

const metaRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const SessionsPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`;

  const { items: sessions, loading } = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.sessions
  );

  const fetchSessions = useCallback(async (): Promise<void> => {
    dispatch(setSessionsLoading(true));
    try {
      const resp = await axios.get(`v2/user/sessions/${asUser}`);
      dispatch(setSessions(resp.data as ActiveSession[]));
    } catch {
      dispatch(setSessionsLoading(false));
    }
  }, [dispatch, asUser]);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  const handleRevoke = useCallback(
    async (jti: string): Promise<void> => {
      try {
        await axios.post(`v2/user/sessions/${jti}/revoke/${asUser}`);
        dispatch(removeSession(jti));
      } catch {
        // silent — future iteration
      }
    },
    [dispatch, asUser]
  );

  const handleLogoutCurrent = useCallback(async (): Promise<void> => {
    await logout();
  }, [logout]);

  const currentSession = sessions.find((s) => s.is_current);
  const otherSessions = sessions.filter((s) => !s.is_current);

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Active Sessions"
        icon={<DevicesIcon />}
        backRoute={backRoute}
        subtitle="Manage where you're signed in"
      />
      <ProxyBanner />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack gap={2.5} sx={{ p: 3 }}>
          {/* Current session */}
          {currentSession &&
            (() => {
              const Icon = getDeviceIcon(currentSession.device_label);
              return (
                <Box sx={cardSx}>
                  <Stack gap={1.5}>
                    {/* Device row */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Icon sx={{ fontSize: 26, color: 'text.secondary' }} />
                        <Stack gap={0.25}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {currentSession.device_label}
                          </Typography>
                          {(currentSession.location_city ||
                            currentSession.location_country) && (
                            <Typography
                              sx={{ fontSize: 12, color: 'text.secondary' }}
                            >
                              {[
                                currentSession.location_city,
                                currentSession.location_country,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                      <Chip
                        label="Current"
                        size="small"
                        sx={{
                          backgroundColor: 'primary.lighter',
                          color: 'primary.dark',
                          fontWeight: 500,
                          fontSize: 12,
                          borderRadius: '12px',
                          height: 'auto',
                          '& .MuiChip-label': { px: 1, py: 0.5 },
                        }}
                      />
                    </Stack>

                    {/* Metadata */}
                    <Stack gap={0.75}>
                      <Box sx={metaRowSx}>
                        <Typography
                          sx={{ fontSize: 12, color: 'text.secondary' }}
                        >
                          Last activity
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          {formatRelative(currentSession.last_used_at)}
                        </Typography>
                      </Box>
                      <Box sx={metaRowSx}>
                        <Typography
                          sx={{ fontSize: 12, color: 'text.secondary' }}
                        >
                          Browser
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          {currentSession.device_label}
                        </Typography>
                      </Box>
                      {currentSession.ip_address && (
                        <Box sx={metaRowSx}>
                          <Typography
                            sx={{ fontSize: 12, color: 'text.secondary' }}
                          >
                            IP Address
                          </Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {currentSession.ip_address}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Log out this (browser) session — only when viewing your own sessions.
                        When a helper is viewing another user's hub (asUser != ''),
                        the "current" session is the helper's own JWT, not the target
                        user's, so the button wouldn't do what the UI implies. */}
                    {asUser === '' && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => void handleLogoutCurrent()}
                        sx={{
                          backgroundColor: 'error.main',
                          borderRadius: '12px',
                          fontWeight: 600,
                          py: 1.5,
                          '&:hover': { backgroundColor: 'error.dark' },
                        }}
                      >
                        Log Out This Session
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })()}

          {/* Other sessions */}
          {otherSessions.length > 0 && (
            <>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                Other Sessions
              </Typography>
              {otherSessions.map((session) => {
                const Icon = getDeviceIcon(session.device_label);
                return (
                  <Box key={session.jti} sx={cardSx}>
                    <Stack gap={1.5}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Icon
                            sx={{ fontSize: 26, color: 'text.secondary' }}
                          />
                          <Stack gap={0.25}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              {session.device_label}
                            </Typography>
                            {(session.location_city ||
                              session.location_country) && (
                              <Typography
                                sx={{ fontSize: 12, color: 'text.secondary' }}
                              >
                                {[
                                  session.location_city,
                                  session.location_country,
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                        <Typography
                          role="button"
                          tabIndex={0}
                          onClick={() => void handleRevoke(session.jti)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              void handleRevoke(session.jti);
                            }
                          }}
                          aria-label={`Revoke session on ${session.device_label}`}
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'error.dark',
                            cursor: 'pointer',
                            outline: 'none',
                            borderRadius: '4px',
                            '&:hover': { textDecoration: 'underline' },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'error.dark',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          Revoke
                        </Typography>
                      </Stack>

                      <Stack gap={0.75}>
                        <Box sx={metaRowSx}>
                          <Typography
                            sx={{ fontSize: 12, color: 'text.secondary' }}
                          >
                            Last activity
                          </Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {formatRelative(session.last_used_at)}
                          </Typography>
                        </Box>
                        <Box sx={metaRowSx}>
                          <Typography
                            sx={{ fontSize: 12, color: 'text.secondary' }}
                          >
                            Browser
                          </Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {session.device_label}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </>
          )}

          {sessions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                No active sessions found.
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default SessionsPage;
